import { LetterLayout, buildLetterHeaderHtml, buildLetterFooterHtml } from './PersonnelLetterTemplate';

interface CarData {
  plateNumber: string;
  chassisNumber: string;
  engineNumber: string;
  manufacturingYear: string;
}

export type LetterLanguage = 'ar' | 'en';

interface LetterText {
  dir: 'rtl' | 'ltr';
  align: 'right' | 'left';
  dateAlign: 'right' | 'left';
  dateLabel: string;
  recipient: (branchName: string) => string;
  greeting: string;
  body: (branchName: string) => string;
  headers: { index: string; plate: string; chassis: string; engine: string; year: string };
  closing: string;
  signature: string;
  title: string;
  formatDate: () => string;
}

const TEXTS: Record<LetterLanguage, LetterText> = {
  ar: {
    dir: 'rtl',
    align: 'right',
    dateAlign: 'left',
    dateLabel: 'تاريخ',
    recipient: (b) => `السيد/ قائد مكتب مخابرات حرس حدود – ${b}`,
    greeting: 'تحية طيبة وبعد ،،،',
    body: (b) => `يرجى من سيادتكم التكرم بعمل اللازم نحو استخراج تصاريح حرس الحدود للوحدة ${b} للسيارات الاتي بياناتهم:`,
    headers: { index: 'م', plate: 'رقم السيارة', chassis: 'رقم الشاسيه', engine: 'رقم الموتور', year: 'سنة الصنع' },
    closing: 'ونحن إذ نشكر حسن تعاونكم المعهود معنا',
    signature: 'إدارة الموارد البشرية',
    title: 'تصاريح حرس الحدود - سيارات',
    formatDate: () => new Date().toLocaleDateString('ar-EG'),
  },
  en: {
    dir: 'ltr',
    align: 'left',
    dateAlign: 'right',
    dateLabel: 'Date',
    recipient: (b) => `To: Commander of Border Guard Intelligence Office – ${b}`,
    greeting: 'Dear Sir,',
    body: (b) => `Kindly take the necessary action to issue Border Guard permits for unit ${b} for the following vehicles:`,
    headers: { index: '#', plate: 'Plate Number', chassis: 'Chassis Number', engine: 'Engine Number', year: 'Manufacturing Year' },
    closing: 'Thank you for your continued cooperation.',
    signature: 'Human Resources Department',
    title: 'Border Guard Permits - Vehicles',
    formatDate: () => new Date().toLocaleDateString('en-GB'),
  },
};

/**
 * The car details table only — what the {{CAR_DETAILS}} placeholder stands
 * for inside a template, so the template's own text is not duplicated.
 */
export const generateCarTableHTML = (
  carData: CarData[],
  language: LetterLanguage = 'ar'
): string => {
  const t = TEXTS[language];
  return `<table style="width: 100%; border-collapse: collapse; margin: 15px 0; direction: ${t.dir};" border="1" cellpadding="8" cellspacing="0">
        <thead>
          <tr style="background-color: #e0e0e0;">
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.index}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.plate}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.chassis}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.engine}</th>
            <th style="border: 1px solid #000; padding: 8px; text-align: center;">${t.headers.year}</th>
          </tr>
        </thead>
        <tbody>
          ${carData.map((car, index) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${index + 1}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${car.plateNumber}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${car.chassisNumber}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${car.engineNumber}</td>
              <td style="border: 1px solid #000; padding: 8px; text-align: center;">${car.manufacturingYear}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`;
};

export const generateCarPreviewHTML = (
  carData: CarData[],
  branchName: string,
  language: LetterLanguage = 'ar'
): string => {
  const t = TEXTS[language];

  return `<div dir="${t.dir}" style="font-family: 'Arial', 'Tahoma', sans-serif; text-align: ${t.align}; line-height: 2;">
      <p style="text-align: ${t.dateAlign}; margin-bottom: 20px;">${t.dateLabel}: ${t.formatDate()}</p>

      <p style="font-weight: bold; margin-bottom: 15px;">${t.recipient(branchName)}</p>

      <p style="margin-bottom: 5px;">${t.greeting}</p>

      <p style="margin-bottom: 15px;">${t.body(branchName)}</p>

      ${generateCarTableHTML(carData, language)}

      <p style="margin-top: 20px;">${t.closing}</p>

      <p style="margin-top: 30px; text-align: ${t.dateAlign};">${t.signature}</p>
    </div>`;
};

export const buildCarPrintHTML = (
  carData: CarData[],
  branchName: string,
  language: LetterLanguage = 'ar',
  layout?: LetterLayout,
  bodyHtml?: string
): string => {
  const t = TEXTS[language];
  const hasFooter = !!layout?.footerHtml?.trim();
  const body = bodyHtml?.trim() || `
    <div class="date">${t.dateLabel}: ${t.formatDate()}</div>

    <p class="recipient">${t.recipient(branchName)}</p>

    <p>${t.greeting}</p>

    <p>${t.body(branchName)}</p>

    <table>
      <thead>
        <tr>
          <th>${t.headers.index}</th>
          <th>${t.headers.plate}</th>
          <th>${t.headers.chassis}</th>
          <th>${t.headers.engine}</th>
          <th>${t.headers.year}</th>
        </tr>
      </thead>
      <tbody>
        ${carData.map((car, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${car.plateNumber}</td>
            <td>${car.chassisNumber}</td>
            <td>${car.engineNumber}</td>
            <td>${car.manufacturingYear}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="footer">
      <p>${t.closing}</p>
    </div>

    <div class="signature">
      <p>${t.signature}</p>
    </div>`;

  return `<!DOCTYPE html>
<html dir="${t.dir}">
  <head>
    <meta charset="UTF-8">
    <title>${t.title}</title>
    <style>
      @page { margin: 20mm; size: A4; }
      body {
        font-family: 'Arial', 'Tahoma', sans-serif;
        font-size: 14px;
        line-height: 2;
        margin: 0;
        padding: 20px;
        ${hasFooter ? 'padding-bottom: 90px;' : ''}
        direction: ${t.dir};
        text-align: ${t.align};
      }
      .date { text-align: ${t.dateAlign}; margin-bottom: 20px; }
      .recipient { font-weight: bold; margin-bottom: 15px; }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 15px 0;
      }
      th, td {
        border: 1px solid #000;
        padding: 8px;
        text-align: center;
      }
      th { background-color: #e0e0e0; font-weight: bold; }
      .footer { margin-top: 30px; }
      .signature { text-align: ${t.dateAlign}; margin-top: 40px; }
      @media print {
        body { padding: 0; ${hasFooter ? 'padding-bottom: 90px;' : ''} }
      }
    </style>
  </head>
  <body>
    ${buildLetterHeaderHtml(layout)}
    ${body}
    ${buildLetterFooterHtml(layout, true)}
  </body>
</html>`;
};
